import { clamp, noop } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useCastContext } from '../../chromecast/cast-context';
import { Cast } from '../../chromecast/hooks/use-cast';
import { useCastController } from '../../chromecast/hooks/use-cast-controller';
import { CastInfo, useCastInfo } from '../../chromecast/hooks/use-cast-info';
import { useMediaState } from '../../chromecast/hooks/use-media-state';
import i18n from '../../i18n';
import { PlayerOptions } from '../../interfaces';
import { useSearchParams } from '../../routes/utils/use-search-params';
import { unloadCurrentLiveProgram } from '../../views/tv/actions';
import { getActiveTracks, getCastTracks, updateCastStatus } from './helpers/ChromeCast';
import { Seekbar } from './seekbar/seekbar';
import './styles/cc-style.scss';
import PlayerControlRow from './UI/PlayerControls/PlayerControlsRow';
import HoverMenu from './UI/PlayerHoverMenu';
import VolumeControl from './UI/PlayerVolumeControl';

type LanguageMap = {
  nosub: string;
  en: string;
  eng: string;
  no: string;
  nb: string;
  nor: string;
  da: string;
  dan: string;
  sv: string;
  se: string;
  smi: string;
  swe: string;
  fi: string;
  fin: string;
  '': 'unknown';
};

const languageMap: LanguageMap = {
  nosub: i18n.t<string>('nosub'),
  en: i18n.t<string>('english'),
  eng: i18n.t<string>('english'),
  no: i18n.t<string>('norwegian'),
  nb: i18n.t<string>('norwegian'),
  nor: i18n.t<string>('norwegian'),
  da: i18n.t<string>('danish'),
  dan: i18n.t<string>('danish'),
  sv: i18n.t<string>('swedish'),
  se: i18n.t<string>('sami'),
  smi: i18n.t<string>('sami'),
  swe: i18n.t<string>('swedish'),
  fi: i18n.t<string>('finnish'),
  fin: i18n.t<string>('finnish'),
  '': 'unknown',
};

interface ChromeCastPlayerProps {
  cast: Required<Cast>;
  info: CastInfo;
}

function ChromeCastPlayer({ cast, info }: ChromeCastPlayerProps) {
  const { player } = useCastContext();
  const controller = useCastController();
  const { t } = useTranslation();

  const search = useSearchParams();
  const position = search.get('position');

  const [hasSetInitialTime, setHasSetInitialTime] = useState(false);

  const [currentTime, setCurrentTime] = useMediaState({
    initialValue: 0,
    onEvent() {
      return cast.media.getEstimatedTime();
    },
    onControlDone(value) {
      cast.message.send({
        type: 'SEEK',
        currentTime: value,
      });
    },
  });

  const [volume, setVolume] = useMediaState({
    initialValue: 0,
    onEvent() {
      return cast.session.getVolume();
    },
    onControlDone(value) {
      if (cast.mediaStatus === 'playing') {
        cast.session.setVolume(value);
      }
    },
  });

  const [isMuted, setIsMuted] = useMediaState({
    initialValue: false,
    onEvent() {
      return player ? player.isMuted : false;
    },
    onControlDone(value) {
      if (cast.mediaStatus === 'playing') {
        cast.session.setMute(value);
      }
    },
  });

  const [message, setMessage] = useState<string | undefined>(undefined);

  //
  // - MARK: Derived state
  const isLive = useMemo(() => info.streamType === 'LIVE', [info.streamType]);
  const title = useMemo(() => info.metadata?.title, [info.metadata?.title]);

  const deviceName = useMemo(() => {
    const device = cast.session.getCastDevice();
    return device.friendlyName ? device.friendlyName : 'Unknown';
  }, [cast.session]);

  const duration = useMemo(() => {
    if (info.streamType === 'LIVE') {
      return info.metadata?.sectionDuration ?? info.duration ?? -1;
    }

    return info.duration ?? -1;
  }, [info.duration, info.metadata?.sectionDuration, info.streamType]);

  const startTime = useMemo(() => {
    if (info.streamType === 'LIVE') {
      return info.metadata?.sectionStartAbsoluteTime ?? -1;
    }

    return 0;
  }, [info]);

  const startAbsoluteTime = useMemo(() => {
    if (info.streamType === 'LIVE') {
      return info.startAbsoluteTime ?? -1;
    }

    return 0;
  }, [info]);

  const dispatch = useDispatch();

  const setActiveTracks = useCallback(
    (activeTrackIds: number[]) => {
      const tracksInfoRequest = new window.chrome.cast.media.EditTracksInfoRequest(activeTrackIds);
      cast.media.editTracksInfo(tracksInfoRequest, noop, (error) => setMessage(error.description ?? undefined));
    },
    [cast.media],
  );

  const cleanupOnDisconnect = useCallback(
    (event: cast.framework.RemotePlayerChangedEvent) => {
      if (event.field === 'isConnected' && !event.value) {
        cast.session.endSession(true);
        dispatch(unloadCurrentLiveProgram());
      }
    },
    [dispatch, cast.session],
  );

  useEffect(() => {
    controller.addEventListener(window.cast.framework.RemotePlayerEventType.ANY_CHANGE, cleanupOnDisconnect);

    updateCastStatus();

    return () => {
      controller.removeEventListener(window.cast.framework.RemotePlayerEventType.ANY_CHANGE, cleanupOnDisconnect);
    };
  }, [controller, cleanupOnDisconnect]);

  const secondsFromLiveEdge = () => {
    let mediaStatus = cast.session.getMediaSession();
    if (!mediaStatus) {
      return 0;
    }
    // eslint-disable-next-line @typescript-eslint/dot-notation
    return Math.abs(Number(currentTime) + Number(mediaStatus?.media?.['startAbsoluteTime'] - moment().unix()));
  };

  const setSubtitle = useCallback(
    (track: chrome.cast.media.Track) => {
      setActiveTracks(track.language === 'nosub' ? [] : [track.trackId]);
    },
    [setActiveTracks],
  );

  const setAudioTrack = useCallback(
    (track: chrome.cast.media.Track) => {
      setActiveTracks([track.trackId]);
    },
    [setActiveTracks],
  );

  const checked = () => {
    return <span className="selection-selected aib-icon">v</span>;
  };

  function audioView(
    showAudios: boolean,
    audioTracks: chrome.cast.media.Track[],
    activeAudioTrackId: number | undefined,
  ) {
    if (!showAudios) {
      return [];
    }
    return audioTracks.map((track) => {
      let isActive = track.trackId === activeAudioTrackId;
      return (
        <li
          key={`${track.trackId} ${track.trackId}`}
          onClick={() => {
            setAudioTrack(track);
          }}
        >
          {isActive ? <strong>{languageMap[track.language]}</strong> : languageMap[track.language]}
          {isActive ? checked() : null}
        </li>
      );
    });
  }

  function subtitleView(
    showSubs: boolean,
    textTracks: chrome.cast.media.Track[],
    activeTextTrackId: number | undefined,
  ): JSX.Element[] {
    if (!showSubs) {
      return [];
    }
    return textTracks.map((track) => {
      let isActive = track.trackId === activeTextTrackId;
      let trackIsChosen = activeTextTrackId;
      let language = languageMap[track.language];
      const trackIsSelected =
        (isActive && trackIsChosen) || (trackIsChosen === undefined && track.language === 'nosub');
      /* eslint-disable @typescript-eslint/dot-notation */
      return (
        <li
          key={`${track.type} ${track.trackId}`}
          onClick={() => {
            setSubtitle(track);
          }}
        >
          {trackIsSelected ? <strong>{language}</strong> : language}
          {track.hasOwnProperty('hardOfHearing') && track['hardOfHearing'] ? '(HOH)' : ''}
          {isActive || trackIsSelected ? checked() : null}
        </li>
      );
      /* eslint-enable @typescript-eslint/dot-notation */
    });
  }

  function audioAndSubView() {
    const tracks = getCastTracks();
    if (!tracks) {
      return undefined;
    }
    const audioTracks = tracks.filter((x) => x.type === 'AUDIO');
    const textTracks = tracks.filter((x) => x.type === 'TEXT');
    const showAudios = audioTracks.length > 1;
    const showSubs = textTracks.length >= 1;

    textTracks.unshift({
      language: 'nosub',
      type: chrome.cast.media.TrackType.TEXT,
      trackId: 0,
      hardOfHearing: false,
    } as unknown as chrome.cast.media.Track);

    const activeTracks = getActiveTracks();
    const activeTextTrackId = activeTracks && activeTracks[1] ? activeTracks[1] : undefined;
    const activeAudioTrackId = activeTracks && activeTracks[0] ? activeTracks[0] : undefined;

    return (
      <HoverMenu
        className={'audio-and-subtitles cc'}
        icon={'ë'}
        optionsType={PlayerOptions.AudioAndSubtitles}
        audios={audioView(showAudios, audioTracks, activeAudioTrackId)}
        subtitles={subtitleView(showSubs, textTracks, activeTextTrackId)}
      />
    );
  }

  // For some reason the types for this are wrong, liveSeekableRange
  // is deprecated, so we should use getEstimatedLiveSeekableRange.
  function getLiveSeekableRange() {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    if (cast.media['getEstimatedLiveSeekableRange']) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      return cast.media['getEstimatedLiveSeekableRange']();
    }
    return cast.media.liveSeekableRange;
  }

  const absoluteCurrentTime = Math.ceil(startAbsoluteTime + currentTime);
  const absoluteDuration = Math.ceil(startTime + duration);

  const formattedCurrentTime = isLive
    ? moment.unix(absoluteCurrentTime).format('HH:mm:ss')
    : controller.getFormattedTime(currentTime);

  const formattedDuration = isLive
    ? moment.unix(absoluteDuration).format('HH:mm:ss')
    : controller.getFormattedTime(duration - currentTime);

  const liveSeekableRange = getLiveSeekableRange();

  const relativeStartTime = clamp(startTime - startAbsoluteTime, 0, Infinity);
  const relativeEndTime = absoluteDuration - startAbsoluteTime;
  const relativeLiveEdge = liveSeekableRange ? liveSeekableRange.end : relativeEndTime;

  const seeker = {
    min: isLive ? relativeStartTime : 0,
    max: isLive ? relativeEndTime : duration,
    value: currentTime,
  };

  // We could use the media status, but it's not reliable.
  // I assume it's because the receiver is quite buggy, so
  // hopefully we will get a better status soon.
  const isLoading = seeker.max <= 0 || seeker.min < 0 || cast.mediaStatus === 'loading';

  useEffect(() => {
    if (hasSetInitialTime || isLoading || !isLive) {
      return;
    }

    const validStatuses = ['loading', 'playing'];

    if (position !== undefined && validStatuses.includes(cast.mediaStatus)) {
      try {
        const positionAsSeconds = parseInt(position, 10);
        const startPosition = relativeStartTime + positionAsSeconds;
        setCurrentTime(startPosition);
        setHasSetInitialTime(true);
      } catch (error) {
        console.error('Could not parse position', error);
      }
    }
  }, [cast.mediaStatus, hasSetInitialTime, isLoading, isLive, position, relativeStartTime, setCurrentTime]);

  const statusText = useMemo(() => {
    switch (cast.mediaStatus) {
      case 'idle':
      case 'loading':
        return isLoading ? t('starting on') : t('playing on');
      case 'paused':
        return t('pause on');
      case 'playing':
      case 'buffering':
        return isLoading ? t('starting on') : t('playing on');
      default:
        return '';
    }
  }, [cast.mediaStatus, t, isLoading]);

  return (
    <div className="cc-player player-controls">
      <div className="cc-player-row cc-player-row-1">
        <div className="cc-player-item">{isLoading ? '00:00:00' : formattedCurrentTime}</div>

        <div className="cc-player-item cc-player-seekbar-item">
          <Seekbar
            className="cc-player-seekbar"
            onChange={(value) => setCurrentTime(value)}
            ghostValue={relativeLiveEdge}
            isLoading={isLoading}
            {...seeker}
          />
        </div>

        <div className="cc-player-item">{isLoading ? '00:00:00' : formattedDuration}</div>
      </div>
      <div className="cc-player-title">
        {statusText} {deviceName}: <span>{message ?? title}</span>
      </div>
      <div className="cc-player-row">
        <PlayerControlRow
          isPlaying={cast.mediaStatus === 'playing'}
          isFullscreen={false}
          isWatchingLive={secondsFromLiveEdge() < 10}
          playPause={() => controller.playOrPause()}
          fastForward={() => setCurrentTime((prev) => prev + 30)}
          rewind={() => setCurrentTime((prev) => prev - 30)}
          volumeControl={
            <VolumeControl
              volume={volume}
              toggleMute={() => setIsMuted((prev) => !prev)}
              changeVolume={(event) => setVolume(parseFloat(event.target.value))}
              muted={isMuted}
            />
          }
          audioAndSubtitlesMenu={audioAndSubView()}
        />
      </div>
    </div>
  );
}

export default function WrappedChromeCastPlayer({ cast }: Pick<ChromeCastPlayerProps, 'cast'>) {
  const info = useCastInfo(cast.session);
  return info ? <ChromeCastPlayer cast={cast} info={info} /> : null;
}
